import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/es';
import { useAuth } from '../auth/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarDays, 
  List, 
  Clock, 
  MapPin, 
  X, 
  CheckCircle2, 
  Plus,
  CalendarCheck,
  Search,
  Info,
  ChevronRight,
  ShieldCheck,
  AlertCircle
} from 'lucide-react';
import { getReservations, updateReservationStatus } from '../../services/api';
import styles from './CreatorCalendar.module.scss';
import clsx from 'clsx';

// Configure moment locale
moment.locale('es');
const localizer = momentLocalizer(moment);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.23, 1, 0.32, 1],
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] }
  }
};

const CreatorCalendar = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewType, setViewType] = useState('calendar');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DD');

      const response = await getReservations({
        start_date: startOfMonth,
        end_date: endOfMonth,
        page_size: 1000
      });

      const reservationsData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setReservations(reservationsData);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('No se pudieron cargar tus reservaciones. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    if (currentUser && currentUser.user_type === 'creator') {
      fetchReservations();
    }
  }, [currentUser, fetchReservations]);

  const handleNavigate = useCallback((newDate) => {
    const newMonth = moment(newDate).month();
    const currentMonth = moment(currentDate).month();
    
    if (newMonth !== currentMonth) {
      setCurrentDate(newDate);
    }
  }, [currentDate]);

  const calendarEvents = useMemo(() => {
    if (!Array.isArray(reservations)) return [];
    return reservations
      .filter(reservation => statusFilter === 'all' || reservation.status === statusFilter)
      .map(reservation => {
        const businessName = reservation.offer_details?.business_name || 'Business';
        const startTime = moment(reservation.start_datetime).format('HH:mm');
        let eventTitle = `${startTime} • ${businessName}`;
        
        if (reservation.status === 'cancelled') {
          eventTitle = `❌ ${eventTitle}`;
        }
        
        return {
          id: reservation.id,
          title: eventTitle,
          start: new Date(reservation.start_datetime),
          end: new Date(reservation.end_datetime),
          status: reservation.status,
          allDay: false,
          resource: reservation
        };
      });
  }, [reservations, statusFilter]);

  const statistics = useMemo(() => {
    const total = reservations.length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const pending = reservations.filter(r => r.status === 'pending').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;
    
    const upcoming = reservations.filter(r => 
      moment(r.start_datetime).isAfter(moment()) && r.status === 'confirmed'
    ).length;

    return { total, confirmed, pending, cancelled, upcoming };
  }, [reservations]);

  const nextReservation = useMemo(() => {
    return reservations
      .filter(r => moment(r.start_datetime).isAfter(moment()) && r.status === 'confirmed')
      .sort((a, b) => moment(a.start_datetime).diff(moment(b.start_datetime)))[0];
  }, [reservations]);

  const eventStyleGetter = (event) => {
    let style = {
      borderRadius: '8px',
      opacity: 0.9,
      color: '#ffffff',
      border: 'none',
      fontWeight: '600',
      fontSize: '0.8rem',
      padding: '4px 8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease'
    };
    
    if (event.status === 'confirmed') {
      style.background = 'linear-gradient(135deg, #c5a46d 0%, #d4af37 100%)';
    } else if (event.status === 'cancelled') {
      style.background = 'rgba(239, 68, 68, 0.2)';
      style.color = 'rgba(255, 255, 255, 0.5)';
      style.border = '1px solid rgba(239, 68, 68, 0.3)';
      style.textDecoration = 'line-through';
    } else {
      style.background = 'rgba(255, 255, 255, 0.05)';
      style.border = '1px solid rgba(255, 255, 255, 0.1)';
    }
    
    return { style };
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar esta reserva?')) return;
    
    try {
      await updateReservationStatus(reservationId, 'cancelled');
      await fetchReservations();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      setError('No se pudo cancelar la reserva. Por favor intenta de nuevo.');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('D [de] MMMM, HH:mm');
  };

  if (loading) return (
    <div className={styles.loadingContainer}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.loader}>
        <div className={styles.spinner} />
        <p>Sincronizando tu agenda...</p>
      </motion.div>
    </div>
  );

  return (
    <motion.div 
      className={styles.calendarPage}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className={styles.container}>
        {/* Header */}
        <motion.div className={styles.header} variants={itemVariants}>
          <div className={styles.headerInfo}>
            <div className={styles.badge}>
                <CalendarDays size={14} />
                <span>Creator Hub</span>
            </div>
            <h1 className={styles.title}>Mi Calendario</h1>
            <p className={styles.subtitle}>Gestiona tus ejecuciones y colaboraciones activas.</p>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
                <span className={styles.statVal}>{statistics.confirmed}</span>
                <span className={styles.statLab}>Confirmadas</span>
            </div>
            <div className={styles.statCard}>
                <span className={styles.statVal}>{statistics.upcoming}</span>
                <span className={styles.statLab}>Próximas</span>
            </div>
          </div>
        </motion.div>

        {/* Quick Access Alert */}
        {nextReservation && (
          <motion.div className={styles.nextAlert} variants={itemVariants}>
            <div className={styles.alertMain}>
                <div className={styles.alertIcon}><CalendarCheck size={24} /></div>
                <div className={styles.alertContent}>
                    <h4>Siguiente Ejecución</h4>
                    <p><strong>{nextReservation.offer_details?.title}</strong> — {formatDateTime(nextReservation.start_datetime)}</p>
                </div>
            </div>
            <button onClick={() => setSelectedEvent(nextReservation)} className={styles.alertBtn}>Ver Detalles</button>
          </motion.div>
        )}

        {/* Controls */}
        <motion.div className={styles.controls} variants={itemVariants}>
          <div className={styles.viewToggle}>
            <button className={clsx(styles.toggleBtn, viewType === 'calendar' && styles.active)} onClick={() => setViewType('calendar')}>
              <CalendarDays size={18} /> <span>Mes</span>
            </button>
            <button className={clsx(styles.toggleBtn, viewType === 'list' && styles.active)} onClick={() => setViewType('list')}>
              <List size={18} /> <span>Lista</span>
            </button>
          </div>

          <div className={styles.actions}>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
                <option value="all">Todos los estados</option>
                <option value="confirmed">Confirmadas</option>
                <option value="pending">Pendientes</option>
                <option value="cancelled">Canceladas</option>
            </select>
            <button className={styles.primaryBtn} onClick={() => navigate('/creator/feed')}>
                <Plus size={18} /> Nueva Reserva
            </button>
          </div>
        </motion.div>

        {/* Content */}
        {error && (
          <div className={styles.errorAlert}>
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}
        <motion.div className={styles.mainContent} variants={itemVariants}>
          {viewType === 'calendar' ? (
            <div className={styles.calendarWrapper}>
              <Calendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '700px' }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={(e) => setSelectedEvent(e.resource)}
                date={currentDate}
                onNavigate={handleNavigate}
                messages={{
                    next: "Sig.", previous: "Ant.", today: "Hoy",
                    month: "Mes", week: "Semana", day: "Día", agenda: "Agenda"
                }}
              />
            </div>
          ) : (
            <div className={styles.listView}>
              {reservations.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}><Search size={48} /></div>
                    <h3>No hay reservaciones</h3>
                    <p>Explora el feed de ofertas para comenzar a colaborar.</p>
                    <button onClick={() => navigate('/creator/feed')} className={styles.primaryBtn}>Explorar Feed</button>
                </div>
              ) : (
                <div className={styles.resGrid}>
                    {reservations.filter(r => statusFilter === 'all' || r.status === statusFilter).map(res => (
                        <div
                          key={res.id}
                          className={clsx(styles.resCard, styles[res.status])}
                          onClick={() => setSelectedEvent(res)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedEvent(res);
                            }
                          }}
                        >
                            <div className={styles.cardTop}>
                                <h4>{res.offer_details?.title}</h4>
                                <div className={styles.cardStatus}>{res.status === 'confirmed' ? <CheckCircle2 size={14} /> : <Clock size={14} />} {res.status}</div>
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.cardInfo}><Clock size={14} /> {formatDateTime(res.start_datetime)}</div>
                                <div className={styles.cardInfo}><MapPin size={14} /> {res.offer_details?.business_name}</div>
                            </div>
                            <div className={styles.cardFooter}>
                                <button className={styles.viewBtn}>Detalles <ChevronRight size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div className={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedEvent(null)}>
            <motion.div className={styles.modal} variants={modalVariants} initial="hidden" animate="visible" exit="exit" onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>{selectedEvent.offer_details?.title}</h3>
                <button className={styles.closeBtn} onClick={() => setSelectedEvent(null)}><X size={20} /></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.detailRow}>
                    <div className={styles.detailIcon}><CheckCircle2 size={20} /></div>
                    <div className={styles.detailContent}>
                        <label>Estado</label>
                        <span className={clsx(styles.statusBadge, styles[selectedEvent.status])}>{selectedEvent.status}</span>
                    </div>
                </div>
                <div className={styles.detailRow}>
                    <div className={styles.detailIcon}><Clock size={20} /></div>
                    <div className={styles.detailContent}>
                        <label>Fecha y Hora</label>
                        <span>{formatDateTime(selectedEvent.start_datetime)} — {moment(selectedEvent.end_datetime).format('HH:mm')}</span>
                    </div>
                </div>
                <div className={styles.detailRow}>
                    <div className={styles.detailIcon}><MapPin size={20} /></div>
                    <div className={styles.detailContent}>
                        <label>Negocio</label>
                        <span>{selectedEvent.offer_details?.business_name}</span>
                    </div>
                </div>
                {selectedEvent.notes && (
                    <div className={styles.detailRow}>
                        <div className={styles.detailIcon}><Info size={20} /></div>
                        <div className={styles.detailContent}>
                            <label>Notas</label>
                            <p>{selectedEvent.notes}</p>
                        </div>
                    </div>
                )}
                
                <div className={styles.modalNotice}>
                    <ShieldCheck size={16} />
                    <p>Las reservas son compromisos serios. Si no puedes asistir, cancela con al menos 24h de antelación.</p>
                </div>
              </div>
              <div className={styles.modalFooter}>
                {['pending', 'confirmed'].includes(selectedEvent.status) && (
                    <button className={styles.cancelBtn} onClick={() => handleCancelReservation(selectedEvent.id)}>Cancelar Reserva</button>
                )}
                <button className={styles.secondaryBtn} onClick={() => setSelectedEvent(null)}>Cerrar</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CreatorCalendar;
